import type { NextPage } from 'next';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getPageListData } from '@src/config/getPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { BottomBannerTypes } from '@src/constants/bottomBannerTypes';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { useIsDisabledCalculator } from '@src/reducers/metadata';
import { Layout as AppLayout } from 'app/Layout';
import { sravniOfferAction, fullDealOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider';
import { SeoComponents } from 'entities/Seo/ui/SeoComponents';
import { AmountFilter } from 'features/AmountFilter';
import ProductList from 'features/ProductList/ui/ProductList/ProductList';
import { AnchorLinkList } from 'shared/ui';
import { Container } from 'shared/ui/Container/Container';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { FullDealEntryPoint } from 'widgets/FullDealEntryPoint';
import { useIsShowFullDealEntrypoint } from 'widgets/FullDealEntryPoint/lib';
import { LightweightCard } from 'widgets/LightweightCard';
import { MobileSubHeader } from 'widgets/MobileSubHeader';

import styles from './PodPtsPage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.ZAIMY_POD_PTS,
  source: 'search',
  sortPanelVisible: true,
  seoLinksVisible: true,
  extraFiltersVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
};

export const PodPtsPage: NextPage = () => {
  const { filter, seo, pathname, hasUtmLabel, isMobile } = useListPage(ListKeys.ZAIMY_POD_PTS);
  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile }), [isMobile]);

  useBestProducts();

  const isDisabledCalculator = useIsDisabledCalculator();

  const isShowFullDealEntrypoint = useIsShowFullDealEntrypoint();

  let afterHeaders: ReactNode = null;

  if (!isDisabledCalculator) {
    afterHeaders = isShowFullDealEntrypoint ? (
      <FullDealEntryPoint heading={seo?.heading} />
    ) : (
      <AmountFilter filters={filter} listKey={ListKeys.ZAIMY_POD_PTS} />
    );
  }

  return (
    <AppLayout seoJsonSchemaConfig={{ listKey: pageConfig.listKey }}>
      <PageContext.Provider value={config}>
        <BrandingHeaders containerClassName={styles.page_header} afterHeaders={afterHeaders} />
        <Container>
          {!hasUtmLabel && <AnchorLinkList />}

          <MobileSubHeader />
          <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
            <ProductList
              gridLayout
              filter={filter}
              pathname={pathname}
              renderCard={(props) => <LightweightCard hasUtmLabel={hasUtmLabel} {...props} />}
            />
          </CardActionProvider>
        </Container>
        {seo && (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            prefooter={seo.prefooter}
            showAnchorLinks={false}
            showSubheader={false}
          />
        )}
        <BottomBannerContainer enabledTypes={[BottomBannerTypes.OUT_OF_PAGE, BottomBannerTypes.SIDEBAR]} />
      </PageContext.Provider>
    </AppLayout>
  );
};

PodPtsPage.getInitialProps = async (ctx: Application.ReduxNextPageContext) => {
  await getPageListData(ctx, {
    key: ListKeys.ZAIMY_POD_PTS,
    defaultFiltersMode: MODES.ZAIMY_POD_PTS,
  });
};
